import React, { useState, useEffect } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Space, Button, Input, Form, Modal, Tooltip, AutoComplete, Flex } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';

import { DeleteFilled, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import { exception } from '@extensions/notification';
import { IOption } from '@extensions/option';

import { useAppDispatch } from '@store/hooks';
import { userLoaded } from '@store/actions';

import { ICargoCarrier } from '@entities/cargo-carrier';

import { CargoCarrierType, enumLabel } from '@enums/cargo-carrier-type';

dayjs.extend(utc);

interface ICargoCarrierProps extends IBaseComponentProps {
    defaultCargoCarrierId: string | undefined;
    type: CargoCarrierType | undefined;
    api: NotificationInstance;
    disabled?: boolean;
    onSelect?: (value: string | undefined) => void;
}

const CargoCarrier = (props: ICargoCarrierProps) => {
    const { defaultCargoCarrierId, type, api, disabled, onSelect, serverFetch } = props;

    const [сargoCarrierForm] = Form.useForm();

    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();

    const [baseCargoCarriers, setBaseCargoCarriers] = useState<Array<ICargoCarrier>>([]);
    const [cargoCarriers, setCargoCarriers] = useState<Array<ICargoCarrier>>([]);
    const [current, setCurrent] = useState<IOption>();
    const [options, setOptions] = useState<Array<IOption>>([]);
    const [сargoCarrierOpen, setCargoCarrierOpen] = useState<boolean>(false);

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);

    useEffect(() => {
        setRefreshRequired(true);
    }, [type]);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);

        setBaseCargoCarriers([]);

        serverFetch('cargocarriers', { method: 'GET', queryParams: { type: type } })
            .then((data: any) => {
                setBaseCargoCarriers(data);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения перевозчиков', ex, () => d(userLoaded(undefined)));
            });
    }, [refreshRequired]);

    useEffect(() => {
        setCargoCarriers(baseCargoCarriers);
    }, [baseCargoCarriers]);

    useEffect(() => {
        const items: Array<IOption> = [];

        cargoCarriers.map((c: ICargoCarrier) => {
            let label: string = c.name || '';

            const newItem: IOption = {
                id: c.id || '',
                value: c.name || '',
                label: (
                    <Flex gap='small'>
                        <Tooltip title='Удалить' placement='left'>
                            <Button
                                variant='link'
                                color='blue'
                                size='small'
                                icon={<DeleteFilled />}
                                hidden={!current || disabled}
                                onClick={() => {
                                    modal.confirm({
                                        title: `Удалить перевозчика "${label}"?`,
                                        icon: <QuestionCircleOutlined />,
                                        okType: 'primary',
                                        okText: 'Удалить',
                                        cancelText: 'Отмена',
                                        onOk: () => {
                                            onDelete(c?.id);
                                        },
                                    });
                                }}
                            />
                        </Tooltip>
                        <div>{label}</div>
                    </Flex>
                ),
            };

            items.push(newItem);
        });

        setOptions(items);
    }, [cargoCarriers]);

    useEffect(() => {
        onSetCurrent(defaultCargoCarrierId);
    }, [defaultCargoCarrierId, options]);

    const onAdd = (entity: ICargoCarrier) => {
        entity.type = type;

        serverFetch(`cargocarriers`, { method: 'POST', bodyData: entity })
            .then(() => {
                сargoCarrierForm.resetFields();
                setCargoCarrierOpen(false);
                setRefreshRequired(true);
                onSelect && onSelect(entity.id);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка добавления поставщика', ex, () => d(userLoaded(undefined)));
            });
    };

    const onDelete = (id: string | undefined) => {
        if (!id) return;

        serverFetch(`cargocarriers/${id}`, { method: 'DELETE' })
            .then(() => {
                onSelect && onSelect(undefined);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления поставщика', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSetCurrent = (id: string | undefined) => {
        if (!id) {
            setCurrent(undefined);
            return;
        }

        const currentOption = options.find((c) => c.id == id);
        setCurrent(currentOption);
    };

    return (
        <>
            <Space.Compact style={{ width: '100%' }}>
                <AutoComplete
                    allowClear
                    value={current?.value || undefined}
                    disabled={disabled}
                    popupMatchSelectWidth={false}
                    options={options}
                    onSelect={(_, e: IOption) => {
                        onSelect && onSelect(e.id);
                    }}
                    onChange={(value: string | undefined) => {
                        if (!value) {
                            onSelect && onSelect(undefined);
                            return;
                        }
                    }}
                    onClear={() => {
                        onSelect && onSelect(undefined);
                    }}
                    showSearch={{
                        onSearch: async (value: string) => {
                            const result = baseCargoCarriers?.filter((p) => p.name?.toUpperCase()?.includes(value.toUpperCase()));

                            setCargoCarriers(result || []);
                        },
                    }}
                >
                    <Input />
                </AutoComplete>

                {!disabled && (
                    <Tooltip title='Добавить'>
                        <Button icon={<PlusOutlined />} onClick={() => setCargoCarrierOpen(true)} hidden={!current || disabled} />
                    </Tooltip>
                )}
            </Space.Compact>

            <Modal
                destroyOnHidden
                title='Добавить транспортного перевозчика'
                open={сargoCarrierOpen}
                okText='ОК'
                onOk={() => сargoCarrierForm.submit()}
                onCancel={() => {
                    сargoCarrierForm.resetFields();

                    setCargoCarrierOpen(false);
                }}
            >
                <Form colon={false} labelCol={{ span: 5 }} wrapperCol={{ span: 17 }} onFinish={onAdd} form={сargoCarrierForm}>
                    <Form.Item required label='Название' name='name' rules={[{ required: true, message: 'Укажите название перевозчика' }]}>
                        <Input
                            onInput={(e) =>
                                ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toUpperCase().trim())
                            }
                        />
                    </Form.Item>
                    <Form.Item label='ИНН' name='inn'>
                        <Input
                            onInput={(e) =>
                                ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toUpperCase().trim())
                            }
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {modalContextHolder}
        </>
    );
};

export default CargoCarrier;
